import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Tender API
export const listTenders = () => api.get("/tenders").then((r) => r.data);
export const getTender = (id) => api.get(`/tenders/${id}`).then((r) => r.data);
export const createTender = (body) => api.post("/tenders", body).then((r) => r.data);
export const updateTender = (id, body) => api.patch(`/tenders/${id}`, body).then((r) => r.data);
export const deleteTender = (id) => api.delete(`/tenders/${id}`).then((r) => r.data);
export const bulkDeleteTenders = (ids) =>
    api.post("/tenders/bulk-delete", { ids }).then((r) => r.data);
export const createTenderFromText = (text, title) =>
    api.post("/tenders/from-text", { text, title }, { timeout: 240000 }).then((r) => r.data);
export const getStats = () => api.get("/tenders/stats").then((r) => r.data);
export const updateOutcome = (id, body) => api.put(`/tenders/${id}/outcome`, body).then((r) => r.data);

export const uploadPliego = (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
        .post(`/tenders/${id}/upload-pliego`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
};

export const analyzeTender = (id) => api.post(`/tenders/${id}/analyze`).then((r) => r.data);
export const retryPliegosHarvest = (id) =>
    api.post(`/tenders/${id}/retry-pliegos-harvest`).then((r) => r.data);
export const generateCostBreakdown = (id, instructions = "") =>
    api
        .post(`/tenders/${id}/generate-cost-breakdown`, { instructions })
        .then((r) => r.data);
export const saveCostItems = (id, items) =>
    api.put(`/tenders/${id}/cost-items`, items).then((r) => r.data);
export const generateAnnex = (id, name, extra_context = "") =>
    api
        .post(`/tenders/${id}/generate-annex`, { name, extra_context })
        .then((r) => r.data);
export const deleteAnnex = (tenderId, annexId) =>
    api.delete(`/tenders/${tenderId}/annex/${annexId}`).then((r) => r.data);
export const updateAnnex = (tenderId, annexId, body) =>
    api.patch(`/tenders/${tenderId}/annex/${annexId}`, body).then((r) => r.data);

export const fetchFileBlob = (path) =>
    api.get(`/files/${path}`, { responseType: "blob" }).then((r) => r.data);

// Company profile
export const getCompanyProfile = () => api.get("/company-profile").then((r) => r.data);
export const putCompanyProfile = (body) => api.put("/company-profile", body).then((r) => r.data);

// Analytics + alerts
export const getAnalyticsOverview = () => api.get("/analytics/overview").then((r) => r.data);
export const getBenchmarkCpv = (code) =>
    api.get(`/analytics/benchmark-cpv?code=${encodeURIComponent(code)}`).then((r) => r.data);
export const getUpcomingAlerts = (days = 14) =>
    api.get(`/alerts/upcoming?days=${days}`).then((r) => r.data);

// Checklist
export const updateChecklistItem = (tenderId, key, body) =>
    api.patch(`/tenders/${tenderId}/checklist-item/${key}`, body).then((r) => r.data);

export const fileUrl = (path) => `${API}/files/${path}`;
export const packageUrl = (id, { includeSolvency = false } = {}) =>
    `${API}/tenders/${id}/download-package${includeSolvency ? "?include_solvency=true" : ""}`;
export const documentDownloadUrl = (storagePath) =>
    `${API}/files/${storagePath}`;

export const uploadPliegoFn = (tenderId, file, onUploadProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
        .post(`/tenders/${tenderId}/upload-pliego`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
            onUploadProgress,
        })
        .then((r) => r.data);
};

export const deleteTenderDocument = (tenderId, storagePath) =>
    api
        .delete(
            `/tenders/${tenderId}/documents/${encodeURIComponent(storagePath)}`,
        )
        .then((r) => r.data);

// PLCSP integration
export const searchPlcsp = (params) =>
    api
        .get("/plcsp/search", { params, timeout: 60000 })
        .then((r) => r.data);
export const getPlcspBreakdown = (params) =>
    api.get("/plcsp/breakdown", { params, timeout: 30000 }).then((r) => r.data);

// Saved searches (per user)
export const listSavedSearches = () =>
    api.get("/plcsp/saved-searches").then((r) => r.data);
export const createSavedSearch = (body) =>
    api.post("/plcsp/saved-searches", body).then((r) => r.data);
export const patchSavedSearch = (sid, body) =>
    api.patch(`/plcsp/saved-searches/${sid}`, body).then((r) => r.data);
export const deleteSavedSearch = (sid) =>
    api.delete(`/plcsp/saved-searches/${sid}`).then((r) => r.data);
export const runSavedSearch = (sid) =>
    api.post(`/plcsp/saved-searches/${sid}/run`).then((r) => r.data);
export const markSavedSearchSeen = (sid) =>
    api.post(`/plcsp/saved-searches/${sid}/mark-seen`).then((r) => r.data);

// Memoria técnica (per tender)
export const memoriaGet = (tenderId) =>
    api.get(`/tenders/${tenderId}/memoria-tecnica`).then((r) => r.data);
export const memoriaExtractRequirements = (tenderId) =>
    api
        .post(`/tenders/${tenderId}/memoria-tecnica/extract-requirements`, null, {
            timeout: 8000,
        })
        .then((r) => r.data);
export const memoriaGenerate = (tenderId, extraInstructions = "") =>
    api
        .post(
            `/tenders/${tenderId}/memoria-tecnica/generate`,
            { extra_instructions: extraInstructions },
            { timeout: 8000 },
        )
        .then((r) => r.data);
export const memoriaRefine = (tenderId, instruction) =>
    api
        .post(
            `/tenders/${tenderId}/memoria-tecnica/refine`,
            { instruction },
            { timeout: 8000 },
        )
        .then((r) => r.data);
export const memoriaDownloadUrl = (tenderId, format) =>
    `${API}/tenders/${tenderId}/memoria-tecnica/download?format=${format}`;
export const memoriaSaveContent = (tenderId, content) =>
    api
        .post(`/tenders/${tenderId}/memoria-tecnica/save-content`, { content })
        .then((r) => r.data);

// Standalone Memoria Técnica (upload PDFs without tender)
export const msList = () =>
    api.get("/memoria-standalone").then((r) => r.data);
export const msCreate = (name, files) => {
    const fd = new FormData();
    fd.append("name", name);
    files.forEach((f) => fd.append("files", f));
    return api
        .post("/memoria-standalone", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 240000,
        })
        .then((r) => r.data);
};
export const msGet = (sid) =>
    api.get(`/memoria-standalone/${sid}`).then((r) => r.data);
export const msExtractRequirements = (sid) =>
    api
        .post(`/memoria-standalone/${sid}/extract-requirements`, null, {
            timeout: 8000,
        })
        .then((r) => r.data);
export const msGenerate = (sid, extraInstructions = "") =>
    api
        .post(
            `/memoria-standalone/${sid}/generate`,
            { extra_instructions: extraInstructions },
            { timeout: 8000 },
        )
        .then((r) => r.data);
export const msRefine = (sid, instruction) =>
    api
        .post(
            `/memoria-standalone/${sid}/refine`,
            { instruction },
            { timeout: 8000 },
        )
        .then((r) => r.data);
export const msSaveContent = (sid, content) =>
    api
        .post(`/memoria-standalone/${sid}/save-content`, { content })
        .then((r) => r.data);
export const msDelete = (sid) =>
    api.delete(`/memoria-standalone/${sid}`).then((r) => r.data);
export const msDownloadUrl = (sid, format) =>
    `${API}/memoria-standalone/${sid}/download?format=${format}`;

export const createDemoTender = () =>
    api.post("/tenders/demo").then((r) => r.data);
export const searchPlcspAi = (body) =>
    api
        .post("/plcsp/search-ai", body, { timeout: 90000 })
        .then((r) => r.data);
export const listPlcspCommunities = () =>
    api.get("/plcsp/communities").then((r) => r.data);
export const listPlcspCommunityCounts = (params) =>
    api.get("/plcsp/community-counts", { params }).then((r) => r.data);
export const listPlcspDocuments = (externalId) =>
    api
        .get("/plcsp/documents", {
            params: { external_id: externalId },
            timeout: 60000,
        })
        .then((r) => r.data);
export const importFromPlcsp = (externalId, withPdfs = false) =>
    api
        .post(
            "/plcsp/import",
            { external_id: externalId, with_pdfs: withPdfs },
            { timeout: 240000 }
        )
        .then((r) => r.data);

// Watchlists & alerts
export const listWatchlists = () =>
    api.get("/watchlists").then((r) => r.data);
export const createWatchlist = (body) =>
    api.post("/watchlists", body).then((r) => r.data);
export const updateWatchlistApi = (id, body) =>
    api.put(`/watchlists/${id}`, body).then((r) => r.data);
export const deleteWatchlist = (id) =>
    api.delete(`/watchlists/${id}`).then((r) => r.data);
export const checkWatchlist = (id) =>
    api.post(`/watchlists/${id}/check`).then((r) => r.data);
export const listWatchlistAlerts = (unreadOnly = false) =>
    api
        .get("/watchlist-alerts", { params: { unread_only: unreadOnly } })
        .then((r) => r.data);
export const markAlertRead = (id) =>
    api.post(`/watchlist-alerts/${id}/read`).then((r) => r.data);
export const markAllAlertsRead = () =>
    api.post("/watchlist-alerts/mark-all-read").then((r) => r.data);

// Contact / leads
export const submitContact = (body) =>
    api.post("/contact", body).then((r) => r.data);
export const listContacts = () =>
    api.get("/admin/contacts").then((r) => r.data);
export const updateContactStatus = (id, status) =>
    api.put(`/admin/contacts/${id}`, { status }).then((r) => r.data);
export const deleteContact = (id) =>
    api.delete(`/admin/contacts/${id}`).then((r) => r.data);

// SaaS customer (tenant) management — super-admin only
export const listCustomers = () =>
    api.get("/admin/customers").then((r) => r.data);
export const createCustomer = (body) =>
    api.post("/admin/customers", body).then((r) => r.data);
export const updateCustomer = (id, body) =>
    api.patch(`/admin/customers/${id}`, body).then((r) => r.data);
export const deleteCustomer = (id, purge = false) =>
    api
        .delete(`/admin/customers/${id}`, { params: { purge_data: purge } })
        .then((r) => r.data);
export const resetCustomerPassword = (id) =>
    api.post(`/admin/customers/${id}/reset-password`).then((r) => r.data);
export const getPlatformCoverage = () =>
    api.get("/admin/platform-coverage").then((r) => r.data);

// Auxiliar Legal — recursos en materia de contratación
export const listLegalCases = () =>
    api.get("/legal/cases").then((r) => r.data);
export const createLegalCase = (body) =>
    api.post("/legal/cases", body).then((r) => r.data);
export const getLegalCase = (id) =>
    api.get(`/legal/cases/${id}`).then((r) => r.data);
export const deleteLegalCase = (id) =>
    api.delete(`/legal/cases/${id}`).then((r) => r.data);
export const uploadLegalDoc = (caseId, file, kind = "otro") => {
    const fd = new FormData();
    fd.append("file", file);
    return api
        .post(`/legal/cases/${caseId}/upload`, fd, {
            params: { kind },
            headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
};
export const deleteLegalDoc = (caseId, docId) =>
    api.delete(`/legal/cases/${caseId}/documents/${docId}`).then((r) => r.data);
export const analyzeLegalCase = (caseId) =>
    api.post(`/legal/cases/${caseId}/analyze`).then((r) => r.data);
export const draftLegalRecurso = (caseId) =>
    api.post(`/legal/cases/${caseId}/draft`).then((r) => r.data);
export const legalDraftDownloadUrl = (caseId, fmt = "pdf") => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}/api/legal/cases/${caseId}/draft/download?fmt=${fmt}`;
};

// Seguimiento de licitaciones adjudicadas
export const listAwardedTenders = () =>
    api.get("/tracking/awarded").then((r) => r.data);
export const markTenderAwarded = (tenderId, body) =>
    api.post(`/tracking/${tenderId}/mark-awarded`, body).then((r) => r.data);
export const unmarkTenderAwarded = (tenderId) =>
    api.post(`/tracking/${tenderId}/unmark-awarded`).then((r) => r.data);
export const getTracking = (tenderId) =>
    api.get(`/tracking/${tenderId}`).then((r) => r.data);
export const createCommitment = (tenderId, body) =>
    api.post(`/tracking/${tenderId}/commitments`, body).then((r) => r.data);
export const updateCommitment = (id, body) =>
    api.patch(`/tracking/commitments/${id}`, body).then((r) => r.data);
export const deleteCommitment = (id) =>
    api.delete(`/tracking/commitments/${id}`).then((r) => r.data);
export const aiExtractCommitments = (tenderId) =>
    api.post(`/tracking/${tenderId}/commitments/ai-extract`).then((r) => r.data);
export const createTrackingTask = (tenderId, body) =>
    api.post(`/tracking/${tenderId}/tasks`, body).then((r) => r.data);
export const updateTrackingTask = (id, body) =>
    api.patch(`/tracking/tasks/${id}`, body).then((r) => r.data);
export const deleteTrackingTask = (id) =>
    api.delete(`/tracking/tasks/${id}`).then((r) => r.data);
export const addTrackingNote = (tenderId, text) =>
    api.post(`/tracking/${tenderId}/notes`, { text }).then((r) => r.data);
export const deleteTrackingNote = (id) =>
    api.delete(`/tracking/notes/${id}`).then((r) => r.data);
export const upcomingTracking = (days = 60) =>
    api.get(`/tracking/calendar/upcoming`, { params: { days } }).then((r) => r.data);
export const trackingOverdueCount = () =>
    api.get(`/tracking/overdue-count`).then((r) => r.data);

// Live web traffic analytics (admin)
export const trafficOverview = () =>
    api.get("/admin/traffic/overview").then((r) => r.data);
export const trafficLive = () =>
    api.get("/admin/traffic/live").then((r) => r.data);
export const trafficSessions = (days = 7, limit = 100) =>
    api.get("/admin/traffic/sessions", { params: { days, limit } }).then((r) => r.data);

// Competitors
export const searchCompetitors = (params) =>
    api.get("/competitors/search", { params }).then((r) => r.data);
export const refreshPlcspFeed = () =>
    api.post("/plcsp/refresh").then((r) => r.data);
export const listSavedCompetitors = () =>
    api.get("/competitors/saved").then((r) => r.data);
export const saveCompetitor = (body) =>
    api.post("/competitors/saved", body).then((r) => r.data);
export const deleteSavedCompetitor = (id) =>
    api.delete(`/competitors/saved/${id}`).then((r) => r.data);
export const checkSavedCompetitor = (id) =>
    api.post(`/competitors/saved/${id}/check`).then((r) => r.data);

// Competitor alerts
export const listCompetitorAlerts = (unreadOnly = false) =>
    api
        .get("/competitor-alerts", { params: { unread_only: unreadOnly } })
        .then((r) => r.data);
export const markCompetitorAlertRead = (id) =>
    api.post(`/competitor-alerts/${id}/read`).then((r) => r.data);
export const markAllCompetitorAlertsRead = () =>
    api.post("/competitor-alerts/mark-all-read").then((r) => r.data);

// Cumplimentar anexos (standalone + per-tender)
export const cumplimentarAnexo = (formData, opts = {}) =>
    api
        .post("/anexos/cumplimentar", formData, {
            timeout: 180000,
            headers: { "Content-Type": "multipart/form-data" },
            ...opts,
        })
        .then((r) => r.data);
export const cumplimentarAnexoTender = (tenderId, formData) =>
    api
        .post(`/tenders/${tenderId}/cumplimentar-anexo`, formData, {
            timeout: 180000,
            headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
export const listFilledAnexos = () =>
    api.get("/anexos/cumplimentados").then((r) => r.data);
export const deleteFilledAnexo = (id) =>
    api.delete(`/anexos/cumplimentados/${id}`).then((r) => r.data);
export const downloadFilledAnexoUrl = (id, fmt) =>
    `${api.defaults.baseURL}/anexos/cumplimentados/${id}/download/${fmt}`;
export const getFilledAnexoSignPayload = (id) =>
    api.get(`/anexos/cumplimentados/${id}/sign-payload`).then((r) => r.data);
export const uploadSignedAnexo = (id, signedB64, signerDn) =>
    api
        .post(`/anexos/cumplimentados/${id}/sign`, {
            signed_pdf_b64: signedB64,
            signer_dn: signerDn || "",
        })
        .then((r) => r.data);
export const getTenderAnnexSignPayload = (tenderId, annexId) =>
    api
        .get(`/tenders/${tenderId}/annexes/${annexId}/sign-payload`)
        .then((r) => r.data);
export const uploadSignedTenderAnnex = (tenderId, annexId, signedB64, signerDn) =>
    api
        .post(`/tenders/${tenderId}/annexes/${annexId}/sign`, {
            signed_pdf_b64: signedB64,
            signer_dn: signerDn || "",
        })
        .then((r) => r.data);
export const downloadSignedTenderAnnexUrl = (tenderId, annexId) =>
    `${api.defaults.baseURL}/tenders/${tenderId}/annexes/${annexId}/download/signed-pdf`;
export const generateTenderBundle = (tenderId) =>
    api.post(`/tenders/${tenderId}/generate-bundle`, {}).then((r) => r.data);
export const getTenderBundleStatus = (tenderId) =>
    api.get(`/tenders/${tenderId}/generate-bundle-status`).then((r) => r.data);
export const chatWithTender = (tenderId, message) =>
    api
        .post(`/tenders/${tenderId}/chat`, { message }, { timeout: 180000 })
        .then((r) => r.data);
export const clearTenderChat = (tenderId) =>
    api.delete(`/tenders/${tenderId}/chat`).then((r) => r.data);
export const exportTenderChatPdfUrl = (tenderId) =>
    `${api.defaults.baseURL}/tenders/${tenderId}/chat/export-pdf`;
export const autoDetectOutcome = (tenderId) =>
    api.post(`/tenders/${tenderId}/auto-detect-outcome`).then((r) => r.data);
export const autoDetectAllOutcomes = () =>
    api.post(`/analytics/auto-detect-all`).then((r) => r.data);

// Admin: users
export const listUsers = () => api.get("/admin/users").then((r) => r.data);
export const createUser = (body) => api.post("/admin/users", body).then((r) => r.data);
export const updateUser = (userId, body) =>
    api.patch(`/admin/users/${userId}`, body).then((r) => r.data);
export const deleteUser = (userId) =>
    api.delete(`/admin/users/${userId}`).then((r) => r.data);
export const listAuditLogs = (params = {}) =>
    api.get("/admin/audit-logs", { params }).then((r) => r.data);
export const getSecurityOverview = () =>
    api.get("/admin/security-overview").then((r) => r.data);


// Solvencia (LCSP Art. 87-90)
export const getSolvencyProfile = () =>
    api.get("/solvency/profile").then((r) => r.data);
export const listSolvencyDocuments = () =>
    api.get("/solvency/documents").then((r) => r.data);
export const uploadSolvencyDocument = (kind, file, onUploadProgress) => {
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    return api
        .post("/solvency/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 240000,
            onUploadProgress,
        })
        .then((r) => r.data);
};
export const deleteSolvencyDocument = (docId) =>
    api.delete(`/solvency/documents/${docId}`).then((r) => r.data);
export const listSolvencyRecords = (type = "") =>
    api.get("/solvency/records", { params: type ? { type } : {} }).then((r) => r.data);
export const createSolvencyRecord = (body) =>
    api.post("/solvency/records", body).then((r) => r.data);
export const updateSolvencyRecord = (id, body) =>
    api.patch(`/solvency/records/${id}`, body).then((r) => r.data);
export const deleteSolvencyRecord = (id) =>
    api.delete(`/solvency/records/${id}`).then((r) => r.data);
export const solvencyExportPdfUrl = (section = "all") =>
    `${API}/solvency/export/pdf${section ? `?section=${section}` : ""}`;
export const solvencyExportXlsxUrl = () => `${API}/solvency/export/xlsx`;

// Solvency-pliego check
export const runTenderSolvencyCheck = (tenderId) =>
    api.post(`/tenders/${tenderId}/solvency-check`, {}, { timeout: 120000 }).then((r) => r.data);

// Company / Users management
export const getCompanyMe = () =>
    api.get("/company/me").then((r) => r.data);
export const listCompanyUsers = () =>
    api.get("/company/users").then((r) => r.data);
export const createCompanyUser = (body) =>
    api.post("/company/users", body).then((r) => r.data);
export const updateCompanyUser = (uid, body) =>
    api.patch(`/company/users/${uid}`, body).then((r) => r.data);
export const deleteCompanyUser = (uid) =>
    api.delete(`/company/users/${uid}`).then((r) => r.data);
export const resetCompanyUserPassword = (uid, new_password) =>
    api
        .post(`/company/users/${uid}/password`, { new_password })
        .then((r) => r.data);

export const getTenderSolvencyCheck = (tenderId) =>
    api.get(`/tenders/${tenderId}/solvency-check`).then((r) => r.data);

// Mi Documentación
export const listMyDocs = (parent_id) =>
    api.get("/my-docs/list", { params: parent_id ? { parent_id } : {} }).then((r) => r.data);
export const createMyDocsFolder = (body) =>
    api.post("/my-docs/folders", body).then((r) => r.data);
export const createMyDocsNote = (body) =>
    api.post("/my-docs/notes", body).then((r) => r.data);
export const uploadMyDocsFile = (file, parent_id) => {
    const fd = new FormData();
    fd.append("file", file);
    if (parent_id) fd.append("parent_id", parent_id);
    return api
        .post("/my-docs/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 240000,
        })
        .then((r) => r.data);
};
export const myDocsFileUrl = (id) => `${API}/my-docs/files/${id}`;
export const getMyDocsItem = (id) =>
    api.get(`/my-docs/items/${id}`).then((r) => r.data);
export const updateMyDocsItem = (id, body) =>
    api.patch(`/my-docs/items/${id}`, body).then((r) => r.data);
export const updateMyDocsFolder = (id, body) =>
    api.patch(`/my-docs/folders/${id}`, body).then((r) => r.data);
export const deleteMyDocsItem = (id) =>
    api.delete(`/my-docs/items/${id}`).then((r) => r.data);
export const deleteMyDocsFolder = (id) =>
    api.delete(`/my-docs/folders/${id}`).then((r) => r.data);

// Comunicaciones
export const listCommunications = (tender_id) =>
    api.get("/communications", { params: tender_id ? { tender_id } : {} }).then((r) => r.data);
export const createCommunication = (body) =>
    api.post("/communications", body).then((r) => r.data);
export const updateCommunication = (id, body) =>
    api.patch(`/communications/${id}`, body).then((r) => r.data);
export const deleteCommunication = (id) =>
    api.delete(`/communications/${id}`).then((r) => r.data);
export const uploadCommunicationAttachment = (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
        .post(`/communications/${id}/attachments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
        })
        .then((r) => r.data);
};
export const communicationAttachmentUrl = (cid, aid) =>
    `${API}/communications/${cid}/attachments/${aid}/download`;
export const deleteCommunicationAttachment = (cid, aid) =>
    api.delete(`/communications/${cid}/attachments/${aid}`).then((r) => r.data);
export const extractCommunicationDeadline = (cid) =>
    api.post(`/communications/${cid}/extract-deadline`, {}, { timeout: 90000 }).then((r) => r.data);

